import React from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useEntitlements } from '../contexts/EntitlementContext';

function ThemeToggle() {
  const nightMode = useAppStore((s) => s.nightMode);
  const toggle = useAppStore((s) => s.toggleNightMode);
  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={nightMode}
      aria-label="Toggle dark mode"
      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card transition-colors"
    >
      <span aria-hidden="true" className="text-lg">{nightMode ? '🌙' : '☀️'}</span>
    </button>
  );
}

function PlanBadge() {
  const { tier } = useEntitlements();
  if (tier === 'free') return null;
  const label = tier === 'premium_plus' ? 'PREMIUM+' : 'PREMIUM';
  const color = tier === 'premium_plus' ? 'bg-purple-600' : 'bg-blue-600';
  return (
    <span className={`${color} text-white text-xs font-bold px-2 py-0.5 rounded-full`}>
      {label}
    </span>
  );
}

const TABS = [
  { to: '/', label: 'Play', icon: '🎮', end: true },
  { to: '/leaderboard', label: 'Ranks', icon: '🏆' },
  { to: '/profile', label: 'Profile', icon: '👤' },
  { to: '/subscribe', label: 'Premium', icon: '✨' },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const showBack = location.pathname !== '/' && location.pathname !== '/play';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-bg pb-20 md:pb-0 md:pl-64 text-gray-900 dark:text-night-text">
      <header className="sticky top-0 z-30 safe-area-top bg-white/90 dark:bg-night-bg/90 backdrop-blur border-b border-gray-100 dark:border-night-border">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showBack && (
              <button
                onClick={() => navigate(-1)}
                aria-label="Back"
                className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card transition-colors"
              >
                <span aria-hidden="true" className="text-lg">←</span>
              </button>
            )}
            <h1 className="font-display text-lg tracking-wide">BLOCK BLAST</h1>
            <PlanBadge />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4">
        <Outlet />
      </main>

      <nav
        className="fixed bottom-0 inset-x-0 z-30 md:hidden safe-area-bottom bg-white dark:bg-night-surface border-t border-gray-100 dark:border-night-border"
        role="tablist"
        aria-label="Primary navigation"
      >
        <div className="grid grid-cols-4 h-16">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              role="tab"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-night-muted'
                }`
              }
            >
              <span aria-hidden="true" className="text-lg">{tab.icon}</span>
              <span className="text-[10px] font-medium uppercase tracking-wide">{tab.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <aside className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-white dark:bg-night-surface border-r border-gray-100 dark:border-night-border p-4 gap-2 safe-area-top safe-area-bottom">
        <h2 className="font-display text-xl mb-4 px-2">BLOCK BLAST</h2>
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                  : 'text-gray-700 dark:text-night-text hover:bg-gray-100 dark:hover:bg-night-card'
              }`
            }
          >
            <span aria-hidden="true" className="text-lg">{tab.icon}</span>
            <span className="font-medium">{tab.label}</span>
          </NavLink>
        ))}
      </aside>
    </div>
  );
}
